import { AlertTriangle, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useDisposeMeters, useReasonChoices } from "../../hooks";

interface DisposeMeterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  meterData: any;
}

const reasons = [
  "Faulty/Defective",
  "End of Life/Age",
  "Upgrade to Smart Meter",
  "Decommissioned",
  "Damage/Accident",
  "Lost",
];

export const DisposeMeterModal = ({
  isOpen,
  onClose,
  onConfirm,
  meterData,
}: DisposeMeterModalProps) => {
  const [selectedReason, setSelectedReason] = useState("");
  const { data: reasonChoices } = useReasonChoices();
  const { mutateAsync: disposeMeter, isPending: isLoading } =
    useDisposeMeters();
  const [error, setError] = useState("");

  const handleDispose = async (payload: any) => {
    if (!selectedReason) {
      setError("Please select a disposal reason.");
      return;
    }
    setError("");
    setSelectedReason("");
    await disposeMeter(payload);
    onConfirm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6 relative">
        <button className="absolute top-4 right-4" onClick={onClose}>
          <X className="h-5 w-5 text-gray-500 hover:text-black" />
        </button>

        <h2 className="text-xl font-semibold mb-4 text-red-600 flex items-center gap-2">
          <Trash2 className="w-5 h-5" />
          Dispose Meter
        </h2>

        <div className="mb-4">
          <div className="mb-4">
            <label className="block font-medium mb-2" htmlFor="disposeReason">
              Reason to Dispose?
            </label>
            <select
              id="disposeReason"
              name="disposeReason"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            >
              <option value="">Select a reason</option>
              {reasonChoices.map((reason) => (
                <option key={reason.key} value={reason.key}>
                  {reason.value}
                </option>
              ))}
            </select>
            {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
          </div>
        </div>

        <div className="bg-gray-100 rounded p-4 grid grid-cols-2 gap-4 text-sm text-gray-800 mb-4">
          <div>
            <p className="text-xs text-gray-500">Last Read Date</p>
            <p className="font-medium">
              {meterData?.currentReadingDate ?? "NA"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Last Reading</p>
            <p className="font-medium">{meterData?.currentReading ?? "NA"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Reading Status</p>
            <p className="font-medium">{meterData?.readingStatus ?? "NA"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Days in Service</p>
            <p className="font-medium">{meterData?.meterAgeInYears ?? "NA"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Device Number</p>
            <p className="font-semibold">{meterData?.deviceNo ?? "NA"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Meter Type</p>
            <p className="font-semibold">
              {meterData?.meterTypeDisplay ?? "NA"}
            </p>
          </div>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 text-yellow-700 text-sm rounded mb-4">
          <AlertTriangle className="inline mr-2 w-4 h-4" />
          This action cannot be undone. The meter will be moved to the disposed
          section and cannot be reassigned.
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              handleDispose({
                meter_ids: [meterData?.id],
                dispose_reason: Number(selectedReason),
              })
            }
            className="px-4 py-2 rounded bg-red-600 text-white flex items-center gap-1"
          >
            <Trash2 className="w-4 h-4" />
            Confirm Disposal
          </button>
        </div>
      </div>
    </div>
  );
};